import { AnalisisLaboratorioResponse } from "../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { AnalisisRegistroResponse } from "../modulos/fertilizacion/response/AnalisisRegistroResponse";

/** Severidad de cada `estado` de parámetro (mayor = peor). Los estados sin
 *  clasificación (`no_range`, `no_crop`) y los `isSentinel` no cuentan para el
 *  "peor". */
const ESTADO_SEVERITY: Record<string, number> = {
  deficient: 5,
  toxic: 4,
  low: 3,
  high: 2,
  optimal: 1,
};

/** Color del semáforo cuando ningún parámetro del registro es clasificable. */
export const URGENCIA_SIN_DATO = "#9CA3AF";

/** Colores de grupo (fondo del botón), del diseño de referencia. */
const GRUPO_COLOR = {
  estructura: "#2563EB",
  mayores: "#0D9488",
  menores: "#16A34A",
  relaciones: "#6D28D9",
} as const;

/** Definición de display de un parámetro que lleva botón en el selector del
 *  mapa: etiqueta corta, nombre completo (title), orden y color de grupo. */
export type ParametroDef = {
  /** Etiqueta corta del botón (ej. "pH", "N", "S. Mg"). */
  simbolo: string;
  /** Nombre completo en español (tooltip `title` del botón). */
  nombre: string;
  /** Orden izquierda→derecha en la fila de botones. */
  orden: number;
  /** Categoría (define el color del botón). */
  grupo: string;
  /** Color del grupo (fondo del botón), en hex. */
  grupoColor: string;
};

/** Catálogo `nombre canónico → display`, en el orden/grupos de las listas
 *  originales del prototipo (estructura → mayores → menores → relaciones).
 *  `texture`/`clay`/`sand`/`silt` no están en las listas → sin botón. */
export const PARAMETRO_CATALOGO: Record<string, ParametroDef> = {
  // Estructura
  ph: { simbolo: "pH", nombre: "Potencial de hidrógeno", orden: 1, grupo: "Estructura", grupoColor: GRUPO_COLOR.estructura },
  electrical_conductivity: { simbolo: "CE", nombre: "Conductividad eléctrica", orden: 2, grupo: "Estructura", grupoColor: GRUPO_COLOR.estructura },
  cec: { simbolo: "CICE", nombre: "Capacidad de intercambio catiónico efectiva", orden: 3, grupo: "Estructura", grupoColor: GRUPO_COLOR.estructura },
  organic_carbon: { simbolo: "COOx", nombre: "Carbono orgánico oxidable", orden: 4, grupo: "Estructura", grupoColor: GRUPO_COLOR.estructura },
  organic_matter: { simbolo: "MO", nombre: "Materia orgánica", orden: 5, grupo: "Estructura", grupoColor: GRUPO_COLOR.estructura },
  bulk_density: { simbolo: "DA", nombre: "Densidad aparente", orden: 6, grupo: "Estructura", grupoColor: GRUPO_COLOR.estructura },
  // Mayores
  total_nitrogen: { simbolo: "N", nombre: "Nitrógeno total", orden: 7, grupo: "Mayores", grupoColor: GRUPO_COLOR.mayores },
  phosphorus: { simbolo: "P", nombre: "Fósforo", orden: 8, grupo: "Mayores", grupoColor: GRUPO_COLOR.mayores },
  potassium: { simbolo: "K", nombre: "Potasio", orden: 9, grupo: "Mayores", grupoColor: GRUPO_COLOR.mayores },
  // Menores
  calcium: { simbolo: "Ca", nombre: "Calcio", orden: 10, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  magnesium: { simbolo: "Mg", nombre: "Magnesio", orden: 11, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  sodium: { simbolo: "Na", nombre: "Sodio", orden: 12, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  exchangeable_acidity: { simbolo: "Al", nombre: "Acidez intercambiable", orden: 13, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  iron: { simbolo: "Fe", nombre: "Hierro", orden: 14, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  manganese: { simbolo: "Mn", nombre: "Manganeso", orden: 15, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  copper: { simbolo: "Cu", nombre: "Cobre", orden: 16, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  zinc: { simbolo: "Zn", nombre: "Zinc", orden: 17, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  boron: { simbolo: "B", nombre: "Boro", orden: 18, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  sulfur: { simbolo: "S", nombre: "Azufre", orden: 19, grupo: "Menores", grupoColor: GRUPO_COLOR.menores },
  // Relaciones
  sat_magnesium: { simbolo: "S. Mg", nombre: "Saturación de magnesio", orden: 20, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  sat_sodium: { simbolo: "S. Na", nombre: "Saturación de sodio", orden: 21, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  sat_aluminum: { simbolo: "S. Al", nombre: "Saturación de aluminio", orden: 22, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  sat_potassium: { simbolo: "S. K", nombre: "Saturación de potasio", orden: 23, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  sat_calcium: { simbolo: "S. Ca", nombre: "Saturación de calcio", orden: 24, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  ratio_ca_mg: { simbolo: "Ca/Mg", nombre: "Relación calcio/magnesio", orden: 25, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  ratio_ca_k: { simbolo: "Ca/K", nombre: "Relación calcio/potasio", orden: 26, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  ratio_mg_k: { simbolo: "Mg/K", nombre: "Relación magnesio/potasio", orden: 27, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
  ratio_camg_k: { simbolo: "(Ca+Mg)/K", nombre: "Relación (calcio+magnesio)/potasio", orden: 28, grupo: "Relaciones", grupoColor: GRUPO_COLOR.relaciones },
};

/** Un parámetro listo para renderizar como botón del selector. */
export type ParametroBoton = {
  /** Clave canónica (= modo de coloreo). */
  key: string;
  /** Etiqueta corta del botón. */
  simbolo: string;
  /** Nombre completo en español (title del botón). */
  nombre: string;
  /** Color de grupo (fondo del botón). */
  grupoColor: string;
};

/** Parámetros del catálogo **presentes** en el análisis (unión de los `nombre`
 *  de todos los registros), ordenados según el catálogo. Es la lista de botones
 *  del selector para ese análisis. */
export function parametrosConBoton(
  analisis: AnalisisLaboratorioResponse,
): ParametroBoton[] {
  const presentes = new Set<string>();
  for (const registro of analisis.registros) {
    for (const parametro of registro.parametros) {
      presentes.add(parametro.nombre);
    }
  }
  return Object.entries(PARAMETRO_CATALOGO)
    .filter(([key]) => presentes.has(key))
    .sort((a, b) => a[1].orden - b[1].orden)
    .map(([key, def]) => ({
      key,
      simbolo: def.simbolo,
      nombre: def.nombre,
      grupoColor: def.grupoColor,
    }));
}

/** Estado de un parámetro → etiqueta en español + variante de `sx-badge`. Los
 *  cuatro "grises" (`categorical`, `sentinel`, `no_data`, `no_range`) van como
 *  neutral: no son diagnóstico, son ausencia de diagnóstico. */
const ESTADO_DISPLAY: Record<string, { label: string; badge: string }> = {
  optimal: { label: "OK", badge: "sx-badge-success" },
  low: { label: "Bajo", badge: "sx-badge-warning" },
  high: { label: "Alto", badge: "sx-badge-info" },
  deficient: { label: "Deficiente", badge: "sx-badge-error" },
  toxic: { label: "Tóxico", badge: "sx-badge-error" },
  acid: { label: "Ácido", badge: "sx-badge-warning" },
  alkaline: { label: "Alcalino", badge: "sx-badge-warning" },
  no_range: { label: "Sin referencia", badge: "sx-badge-neutral" },
  no_crop: { label: "Sin cultivo", badge: "sx-badge-neutral" },
  no_data: { label: "Sin dato", badge: "sx-badge-neutral" },
  sentinel: { label: "No medido", badge: "sx-badge-neutral" },
  categorical: { label: "Textual", badge: "sx-badge-neutral" },
};

/** Etiqueta + badge del estado de un parámetro (fallback: la clave cruda). */
export function estadoDisplay(estado: string | null): {
  label: string;
  badge: string;
} {
  return (
    ESTADO_DISPLAY[estado ?? ""] ?? {
      label: estado ?? "—",
      badge: "sx-badge-neutral",
    }
  );
}

/** Nombre en español del parámetro (del catálogo); la clave cruda si no está
 *  catalogado (ej. `texture`, `clay`). */
export function parametroNombre(nombre: string): string {
  return PARAMETRO_CATALOGO[nombre]?.nombre ?? nombre;
}

/** Óxidos con subíndice para mostrar (`P2O5` → `P₂O₅`). */
const NUTRIENTE_SIMBOLO: Record<string, string> = {
  P2O5: "P₂O₅",
  K2O: "K₂O",
  SO3: "SO₃",
};

/** Símbolo del nutriente listo para render. */
export function nutrienteSimbolo(nutrient: string): string {
  return NUTRIENTE_SIMBOLO[nutrient] ?? nutrient;
}

/** `true` si la unidad es real y no un placeholder del extractor
 *  (`pH_unit`, `—`, vacío). */
export function unidadVisible(unidad: string): boolean {
  return !!unidad && unidad !== "—" && !unidad.includes("_unit");
}

/** Severidad agregada del lote (`registro.severityLevel`, la resuelve el server
 *  al generar la recomendación) → color y etiqueta del semáforo. */
const SEVERIDAD: Record<string, { color: string; label: string }> = {
  critical: { color: "#DC2626", label: "Enmienda crítica" },
  deficient: { color: "#F59E0B", label: "Con deficiencias" },
  maintenance: { color: "#16A34A", label: "Mantenimiento" },
};

/** Color del dot de severidad; gris si el lote todavía no tiene diagnóstico. */
export function severidadColor(severityLevel: string | null): string {
  return SEVERIDAD[severityLevel ?? ""]?.color ?? URGENCIA_SIN_DATO;
}

/** Etiqueta en español de la severidad agregada del lote. */
export function severidadLabel(severityLevel: string | null): string {
  return SEVERIDAD[severityLevel ?? ""]?.label ?? "Sin diagnóstico";
}

/** Tipo de análisis en español (el server lo manda como clave en inglés). */
export function tipoAnalisisLabel(tipoAnalisis: string): string {
  switch (tipoAnalisis) {
    case "soil":
      return "Edáfico";
    case "foliar":
      return "Foliar";
    default:
      return tipoAnalisis || "—";
  }
}

/** Color de "urgencia" de un registro = el `color` (ya resuelto por el server)
 *  del parámetro con peor `estado`, ignorando los `isSentinel` y los estados
 *  sin rango/cultivo; gris si no hay ninguno clasificable. Lo usa el dot del
 *  sidebar. */
export function urgenciaColor(registro: AnalisisRegistroResponse): string {
  let peorColor: string | null = null;
  let peorSeveridad = 0;
  for (const parametro of registro.parametros) {
    if (parametro.isSentinel) continue;
    const severidad = ESTADO_SEVERITY[parametro.estado ?? ""] ?? 0;
    if (severidad > peorSeveridad) {
      peorSeveridad = severidad;
      peorColor = parametro.color;
    }
  }
  return peorColor ?? URGENCIA_SIN_DATO;
}
